import type { DetectedSequencingPlatform } from '../../Util/sequencingPlatformDetection.ts'
import type { ReadLayout, ReadLayoutConfidence, Unmated } from '../../Util/orderSettingDetection/readLayoutDetection.ts'
import type { PrimerTrimVerdict } from '../../Util/orderSettingDetection/primerTrimDetection.ts'
import type { MultiplexVerdict } from '../../Util/orderSettingDetection/multiplexDetection.ts'
import type { DetectionConfidence } from '../../Util/orderSettingDetection/sampleConfigurationClassifier.ts'
import type { SampleConfigurationTypeKey } from '../../Generated/SampleConfigurationType.ts'
import type { TargetTaxonomyKey } from '../../Generated/TargetTaxonomy.ts'
import type { ProcessingTypeKey } from '../../Generated/ProcessingType.ts'
import type { RedlistModeKey } from '../../Generated/RedlistMode.ts'
import type { TargetRegionKey } from '../../Generated/TargetRegion.ts'
import type { ValidationIssue } from '../Order/ValidationIssue.ts'
import type { IOrder } from '../Order/IOrder.ts'
import type { ISamplePool } from '../SamplePool/ISamplePool.ts'
import type { IPrimer } from '../Primer/IPrimer.ts'
import type { OrderCreditCost } from '../../Generated/OrderCreditCost.ts'
import type { OrderSubmissionPreview } from '../../Generated/OrderSubmissionPreview.ts'
import type { SequencingPlatformPreview } from '../../Generated/SequencingPlatformPreview.ts'
import type { ReadLengthSummary } from '../../Util/orderSettingDetection/readLengths.ts'

/** What the browser can tell about a dropped file without keeping the file: enough to recognise it when dropped again. */
export type FileIdentity = { name: string; size: number; lastModified: number }

export const fileKeyOf = (file: FileIdentity): string => `${file.size}:${file.lastModified}:${file.name}`

export type WizardFileKind = 'fastq' | 'sheet' | 'other'

/**
 * `pending`: accepted and waiting for the order, the pool or a free upload slot. `needs-readd`: the
 * browser lost the file handle (a reload), so the user has to drop the file again.
 */
export type WizardUploadStatus = 'pending' | 'uploading' | 'uploaded' | 'failed' | 'needs-readd'

export type WizardFileUpload = {
  status: WizardUploadStatus
  progress: number
  error?: string
  /** Shown beside a running upload, e.g. while a transient failure waits for its next attempt. */
  notice?: string
  /** The server's id for the uploaded file, which pool items reference as `forwardFileId`/`reverseFileId`. */
  serverFileId?: string
}

export type WizardFile = FileIdentity & {
  key: string
  kind: WizardFileKind
  /** Known only from the order (a resumed order): size and date unknown, recognised by name when dropped again. */
  fromServer?: true
  platform?: DetectedSequencingPlatform
  /** Why the file is not uploaded; a rejected file carries no `upload`. */
  rejection?: string
  upload?: WizardFileUpload
}

/** `removed`: the item was deleted in the classic view; the wizard does not create it again. */
export type WizardItemStatus = 'waiting' | 'creating' | 'created' | 'failed' | 'removed'

export type EstimateConfidence = 'high' | 'low' | 'none'

/** One side of a sample's exact primer as estimated from its reads, bounded to what is shown and compared. */
export type PrimerSideEstimate = {
  /** Tag + gene primer as the reads show it, the gene part spelled the lab's way where its history says how. */
  primer: string | null
  /** The bases in front of the gene primer (tag, possibly spacer); `''` means untagged. */
  prefix: string | null
  /** The gene primer's spelling used in `primer`. */
  gene: string | null
  /** The gene spelling came from the lab's own earlier orders rather than the registry. */
  spelledByLab: boolean
  /** Share of the reads carrying the gene primer whose prefix is the dominant one. */
  support: number
  /** Reads the gene primer was found on; absent on estimates made before it was kept. */
  located?: number
  /** `prefix` is a tag behind a variable heterogeneity spacer. */
  spacer?: boolean
  confidence: EstimateConfidence
  reason: string
}

export type PrimerEstimate = { forward: PrimerSideEstimate; reverse: PrimerSideEstimate }

/** The distinct tag pairs in front of the gene primers of an archive's read pairs. */
export type ArchiveTagCensus = {
  /** Read pairs examined, and those with both gene primers located. */
  pairs: number
  located: number
  /** Frequent tag pairs (≥1% of located pairs), most frequent first; share of located pairs. */
  tags: { forwardTag: string; reverseTag: string; share: number }[]
}

/** What was written into one item beyond its files: sheet data and/or estimated primers. */
export type SheetApplyStatus = {
  status: 'pending' | 'done' | 'failed'
  error?: string
  primerSource?: 'sheet' | 'manual' | 'estimate'
  /** The sheet revision whose row was written; a later sheet or a changed column choice is written again. */
  sheetKey?: string
  /** The primers written, `forward\u0000reverse`; other primers are written again. */
  primers?: string
  /** The item's type when written: the classic import gives only a sample a metadata row, so a control turned sample is written again. */
  itemType?: WizardItemType
  /** The metadata fields this write sets. */
  fields?: string[]
}

export type WizardItemType = 'SAMPLE' | 'NEGATIVE_CONTROL'

export type PrimerDiagnosisKind = 'PRIMERS_PRESENT' | 'PRIMERS_ABSENT' | 'OTHER_PRIMER' | 'ONE_PRIMER_MISSING' | 'TAG_MISMATCH' | 'UNKNOWN'

/** What one sample's reads say about the primers declared for it. */
export type SampleDiagnosis = {
  diagnosis: PrimerDiagnosisKind
  reason: string
}

/** One pool item to be: a mate pair, or a single file for merged and Nanopore reads. */
export type WizardSampleGroup = {
  /** The forward file's key. */
  key: string
  forward: string
  reverse?: string
  /** What the item should be; the server's item follows it (created as, or converted to). */
  itemType?: WizardItemType
  /**
   * `user` once the user toggled it, here or in the classic view; `sheet` when the sheet's type column set it,
   * which beats the names (`auto`). Nothing automatic overrides `user`.
   */
  typeSource?: 'auto' | 'sheet' | 'user'
  /** 0-based pool; absent means the first. */
  pool?: number
  /** The forward file's sequencing run from its first headers, for suggesting pools; `''` when unknown. */
  run?: string
  /** Exact primers the lab entered or pasted in the wizard, per side; they beat the sheet's and the estimate. */
  manualPrimers?: { forward?: string; reverse?: string }
  /** The item on the server already carries both primers (set in the classic view, found on reconcile). */
  primersOnServer?: boolean
  item: { status: WizardItemStatus; itemId?: string; error?: string; itemType?: WizardItemType; poolId?: string }
  diagnosis?: SampleDiagnosis
  /** The sheet file and primer columns the diagnosis was made against (`primerRevision`). */
  diagnosedFor?: string
  /** An archive (several tagged samples in one file pair): the items its sheet samples became, by sample key. */
  archiveItems?: Record<string, { itemId: string; itemType?: WizardItemType }>
  /** The user's sample/control choice per archive sample key; beats the automatic label. */
  archiveOverrides?: Record<string, WizardItemType>
  /** The tag pairs an archive's reads carry: experimental, shown only, never written. */
  archiveTags?: ArchiveTagCensus
  estimate?: PrimerEstimate
  applied?: SheetApplyStatus
  /**
   * The metadata fields the wizard's writes may have set on the item; a later write without one clears
   * it, so a corrected mapping or a removed sheet leaves nothing stale. Only what a write confirmed is let go.
   */
  writtenFields?: string[]
  /** The sheet file and primer columns the estimate was last checked against, so each check is reported once. */
  validatedFor?: string
  /**
   * `reverse` joined the item after it was made (the order became paired-end): it is set through the
   * attach-reverse-file op. `absorbItemId`: the item the R2 file became on its own, deleted by the same op.
   */
  reverseAttach?: { status: 'pending' | 'failed'; error?: string; absorbItemId?: string }
  /** The item's R2 was set or taken away in the classic view. */
  reverseFromClassic?: true
}

/** One group's primer entries; an absent side is left as it is, `null` or blank takes the entry back. */
export type PrimerEdit = { key: string; forward?: string | null; reverse?: string | null }

/**
 * A group the lab removed whose item may be on the server: the group and its files are gone from the
 * draft, this record stays until the server no longer holds the item, so a failed removal stays visible.
 */
export type WizardRemoval = {
  groupKey: string
  name: string
  /** The group's item and an archive's further items, as far as the draft knew them. */
  itemIds: string[]
  /** The forward file's server id: finds an item whose creation answer the draft never saw. */
  serverFileId?: string
  /** Every file of the group: no new item takes one of them while the old item may still hold it. Absent on older drafts. */
  serverFileIds?: string[]
  status: 'pending' | 'failed'
  error?: string
}

/** An uploaded file the order keeps that no item uses; `size` is unknown for a file known only from the order. */
export type WizardReleasedFile = { name: string; size?: number; serverFileId: string }

/** A group the lab removed, as it was: "Re-add", or dropping its files again, brings it back with the lab's choices. */
export type WizardRemovedGroup = {
  name: string
  /** Its files as the draft held them, uploads included. */
  files: WizardFile[]
  group: Pick<WizardSampleGroup, 'key' | 'forward' | 'reverse' | 'itemType' | 'typeSource' | 'pool' | 'run' | 'manualPrimers' | 'archiveOverrides'>
  /** The lab closed the note; the group still comes back when its files are dropped again. */
  dismissed?: boolean
}

/** One registry row folded into a pair: its `forward / reverse` name and gene sequences (adapters stripped). */
export type PairRegistration = {
  name: string
  forward: string
  reverse: string
  /** Samples the lab's earlier orders carried this registration's primers on; absent when none or unknown. */
  samples?: number
}

export type KnownPairSummary = {
  first: string
  second: string
  amplicons: TargetTaxonomyKey[]
  /** `forward / reverse` names of the registrations folded into the pair, in the order of `registrations`. */
  names: string[]
  /**
   * The registrations themselves, most used on the lab's earlier orders first, then in registry order.
   * Absent on drafts summarised before they were kept.
   */
  registrations?: PairRegistration[]
  /** Which side the lab wrote as forward on most samples of its earlier orders; absent without such history. */
  labForward?: 'first' | 'second'
  /** The registered spellings of each side, most registered first; what an exact primer is written with. */
  firstVariants: string[]
  secondVariants: string[]
}

export type PrimerDiscovery =
  | {
      status: 'found'
      pair: KnownPairSummary
      reason: string
      /** Identified once R2 files joined reads that showed one primer only. */
      fromMates?: true
    }
  | { status: 'mixed'; pairs: KnownPairSummary[]; reason: string }
  | {
      /** The reads show one primer only, and which registered pair it belongs to is not on them. */
      status: 'partial'
      /** How the registry names the primer the reads show. */
      side: 'forward' | 'reverse'
      /** Its registered names, most registered first, then the more specific. */
      primerNames: string[]
      candidates: KnownPairSummary[]
      reason: string
    }
  | { status: 'none'; reason: string }
  | { status: 'registry-unavailable'; reason: string }

/** What the first files said, bounded to summaries so it can be stored. */
export type DiscoverySummary = {
  platform: DetectedSequencingPlatform
  /**
   * `pairs`: mate pairs found, which still count when too few files pair up for a verdict. `unmated`: no
   * layout fits — no file pairs up, or the reads veto the name pairs, or at most half the files pair — so
   * the files' partners may be missing, or they are merged reads.
   */
  layout: { value: ReadLayout; confidence: ReadLayoutConfidence; reason: string; pairs: number; unmated?: Unmated }
  primers: PrimerDiscovery
  trimming: PrimerTrimVerdict
  multiplexing: MultiplexVerdict
  classifier: {
    configuration: SampleConfigurationTypeKey | 'UNKNOWN'
    confidence: DetectionConfidence | null
    reason: string
    /** Of the files judged, how many the nearest registered pair opens partly: a lean towards primers on. */
    nearPairFiles?: number
    judgedFiles?: number
  }
  filesScanned: number
  /** Sequencing runs named by the scanned files' read headers; information only, never a reason to split pools. */
  runs?: { count: number }
  /** Of the scanned files no mate pairs with, for the review's read-length check. */
  readLengths?: ReadLengthSummary
}

/** `undetermined`: the files did not tell, and a question asks before the order is created. */
export type SettingSource = 'detected' | 'fallback' | 'default' | 'answered' | 'entered' | 'undetermined'

/** The settings the order is created with, each with where it came from. */
export type PlannedOrderSettings = {
  /** `via: 'sheet'`: a low-confidence "trimmed" the sheet's registered primers confirmed, so another sheet decides it again. */
  sampleConfiguration: { value: SampleConfigurationTypeKey; source: SettingSource; reason: string; via?: 'sheet' }
  /** `via: 'sheet'`: the sheet's primers decided it, so another sheet decides it again. */
  targetTaxonomy: { value: TargetTaxonomyKey; source: SettingSource; reason: string; via?: 'sheet' }
  processingType: { value: ProcessingTypeKey; source: SettingSource; reason: string }
  /** The organisation's default processing type does not cover the detected target; asked, never switched silently. */
  processingTypeConflict?: { preferred: ProcessingTypeKey; allowedTaxa: TargetTaxonomyKey[] }
  /** The conflict the lab answered, kept so the answer can be changed. */
  answeredConflict?: { preferred: ProcessingTypeKey; allowedTaxa: TargetTaxonomyKey[] }
  /** Set for CO1 - Animal only; the BOLD reference database follows it. */
  targetRegion?: TargetRegionKey
  /** The region the lab chose; kept while the target is something else, and taken again once it is CO1 - Animal. */
  targetRegionEntered?: TargetRegionKey
  allowSingletons: boolean
  /** `entered` once the lab set it in the wizard; absent means the default. */
  allowSingletonsSource?: 'entered'
  redlistMode: RedlistModeKey
}

/** A setting the lab changes in the wizard once the order exists. */
export type SettingChange =
  | { setting: 'targetTaxonomy'; value: TargetTaxonomyKey }
  | { setting: 'processingType'; value: ProcessingTypeKey }
  | { setting: 'allowSingletons'; value: boolean }
  | { setting: 'targetRegion'; value: TargetRegionKey }
  /** Only between trimmed and untrimmed of the same layout: the order update re-flags the items. */
  | { setting: 'sampleConfiguration'; value: SampleConfigurationTypeKey }

/** The lab's target contradicts the primers submit would validate it with: they are registered only for other amplicons. */
export type TargetCheck = { target: TargetTaxonomyKey; forward: string; reverse: string; registeredFor: TargetTaxonomyKey[] }

/** Settings changed in the wizard and not yet in the order; `revision` counts the changes, so a write answers only its own. */
export type SettingsWrite = {
  status: 'pending' | 'failed'
  revision: number
  error?: string
  /** A format switch to write; without it the order's format is sent as it holds it. */
  sampleConfiguration?: SampleConfigurationTypeKey
}

/** Which sheet column feeds which role. Every field is a column header. */
export type SheetMapping = {
  fileForward?: string
  fileReverse?: string
  sampleId?: string
  sampleName?: string
  forwardPrimer?: string
  reversePrimer?: string
  forwardTag?: string
  reverseTag?: string
  /** Sample or negative control per row. */
  itemType?: string
  /** Which pool each row's sample goes in. */
  pool?: string
  /** Metadata field → column, for the fields the classic import knows. */
  metadata: Partial<Record<SheetMetadataField, string>>
  /** Why each mapped column was taken, for showing next to the mapping. */
  reasons: Record<string, string>
}

export type SheetMetadataField =
  | 'coordinatesMerged'
  | 'coordinatesLat'
  | 'coordinatesLon'
  | 'samplingDate'
  | 'samplingStart'
  | 'samplingEnd'
  | 'matrixType'
  | 'extractionKit'
  | 'trapType'
  | 'collectionType'
  | 'sequencer'
  | 'sequencingChemistry'
  | 'sequencingDepth'
  | 'localityType'
  | 'habitatType'

/** What becomes of a sheet column the mapping did not claim: a custom attribute (default), a metadata field, or nothing. */
export type ExtraColumnTarget = 'custom' | 'drop' | SheetMetadataField

export type SheetState = {
  fileKey: string
  name: string
  headers: string[]
  rows: Record<string, string>[]
  mapping: SheetMapping
  /** Every column the mapping left unclaimed, with the user's choice for it. */
  extraColumns: Record<string, ExtraColumnTarget>
  /** Bumped on every column choice, so written items are written again. */
  revision: number
  notes: string[]
  /** The registered pairs the sheet's primers name, for the columns at `primerRevision`; `null`: none. */
  registeredPrimers?: { revision: string; found: SheetPrimerPairs | null }
}

/** The registered primer pairs a sheet's rows name: their amplicons and registration names. */
export type SheetPrimerPairs = { amplicons: TargetTaxonomyKey[]; names: string[] }

/** A sheet as browser storage keeps it: its identity and the column choices, never its rows. */
export type SheetRef = Pick<SheetState, 'fileKey' | 'name' | 'mapping' | 'extraColumns' | 'revision'>

/** Groups the file names suggest (well positions, or sample numbers per run), offered as pools. */
export type PoolSuggestion = {
  /** 0-based group per group key. */
  pools: Record<string, number>
  count: number
  /** `sheet`: the sample sheet's pool column, offered because its groups do not pass the control rule on their own. */
  method: 'wells' | 'sample-numbers' | 'sheet'
  /** Suggested groups without a negative control: the split is then low confidence. */
  withoutControl: number[]
  /** Why a sheet's pool column is offered rather than applied. */
  reason?: string
}

export type WizardQuestion =
  | {
      id: string
      kind: 'processing-type-conflict'
      /** Order creation waits for the answer. */
      blocking: true
      preferred: ProcessingTypeKey
      detectedTaxon: TargetTaxonomyKey
      allowedTaxa: TargetTaxonomyKey[]
    }
  | { id: string; kind: 'sheet-key-column'; blocking: false; reason: string }
  | { id: string; kind: 'controls-none-found'; blocking: false }
  | { id: string; kind: 'controls-too-many'; blocking: false; flagged: number; total: number }
  | {
      id: string
      kind: 'pools'
      blocking: false
      /** Groups the file names suggest, offered and never preselected; absent when the items already sit in several pools. */
      suggestion?: PoolSuggestion
    }
  | {
      id: string
      kind: 'primers-missing'
      blocking: false
      groupKeys: string[]
      controls: number
      /** Primers to fill the gaps with, when the order's other items suggest them. */
      suggestion?: { forward: string; reverse: string; kind: 'shared' | 'gene' }
      /** A sample sheet was dropped for this draft (dropping it again may bring the primers). */
      sheetUsed: boolean
    }
  | { id: string; kind: 'format-multiplex'; blocking: true; reason: string }
  | { id: string; kind: 'archive-needs-sheet'; blocking: false; archives: number }
  | {
      id: string
      kind: 'read-layout'
      blocking: true
      files: number
      /** The mate the unpaired files hold. */
      mate: 1 | 2
      /** The fixed length their reads share, as raw reads do; absent when the lengths vary like merged reads. */
      rawLength?: number
      trimming: PrimerTrimVerdict
    }
  | {
      id: string
      kind: 'primer-trimming'
      blocking: true
      merged: boolean
      /** What the reads lean to, when they do: a registered pair nearly opens most files. */
      lean?: 'on'
    }
  | {
      id: string
      kind: 'target-taxon'
      blocking: true
      reason: string
      /** Amplicons the registry names for the primers seen, when any. */
      suggestions: TargetTaxonomyKey[]
    }
  | {
      id: string
      kind: 'target-conflict'
      blocking: true
      current: TargetTaxonomyKey
      /** Where the current target came from: the reads' primers, or the lab. */
      from: 'reads' | 'lab'
      sheet: SheetPrimerPairs
    }
  | {
      id: string
      kind: 'target-reads-conflict'
      blocking: true
      current: TargetTaxonomyKey
      /** The pair the R2 files settled. */
      reads: SheetPrimerPairs
    }

export type QuestionAnswer =
  | { kind: 'processing-type-conflict'; choice: 'metabarcoding' }
  | { kind: 'processing-type-conflict'; choice: 'change-taxon'; taxon: TargetTaxonomyKey }
  | { kind: 'sheet-key-column'; mapping: Partial<Pick<SheetMapping, 'fileForward' | 'fileReverse' | 'sampleId' | 'sampleName'>> }
  | { kind: 'controls-none-found'; choice: 'none' | 'mark' }
  | { kind: 'controls-too-many'; choice: 'accept' | 'mark' }
  | { kind: 'primers-missing'; forward: string; reverse: string; groupKeys: string[] }
  | { kind: 'pools'; choice: 'one' }
  | { kind: 'pools'; choice: 'assign'; pools: Record<string, number>; source: 'manual' }
  | { kind: 'pools'; choice: 'assign'; pools: Record<string, number>; source: 'suggested'; method: PoolSuggestion['method'] }
  | { kind: 'format-multiplex'; choice: 'single' | 'combined' }
  | { kind: 'read-layout'; choice: 'merged'; trimmed: boolean }
  | { kind: 'read-layout'; choice: 'add-mates' }
  | { kind: 'primer-trimming'; trimmed: boolean }
  | { kind: 'target-taxon'; taxon: TargetTaxonomyKey }
  | { kind: 'target-conflict'; choice: 'sheet'; taxon: TargetTaxonomyKey }
  | { kind: 'target-conflict'; choice: 'keep' }
  | { kind: 'target-reads-conflict'; choice: 'reads'; taxon: TargetTaxonomyKey }
  | { kind: 'target-reads-conflict'; choice: 'keep' }

export type DraftPhase = 'start' | 'detecting' | 'creating' | 'active' | 'error'

export type DraftState = {
  id: string
  name: string
  description?: string
  createdAt: string
  /** When order creations were sent whose answers may have been lost; a retry looks for an order made then first. */
  creationAttempts?: string[]
  phase: DraftPhase
  error?: string
  orderId?: string
  poolIds: string[]
  files: Record<string, WizardFile>
  fileOrder: string[]
  groups: Record<string, WizardSampleGroup>
  discovery?: DiscoverySummary
  settings?: PlannedOrderSettings
  settingsWrite?: SettingsWrite
  /** The last target the lab chose on Files, when the order's primers contradict it. */
  targetCheck?: TargetCheck
  /** Settings the order holds otherwise than the draft had them: changed in the classic view, and taken over. */
  changedInClassic?: SettingChange['setting'][]
  sheet?: SheetState
  /** After a reload: the sheet to drop again, with the column choices to restore when it comes back. */
  sheetRef?: SheetRef
  /** The sheet a pasted block replaced, until it is restored or the note dismissed; never kept in browser storage. */
  replacedSheet?: SheetState
  /** The negative-control answer, once given; the automatic decision is recomputed from the names. */
  controlsAnswer?: 'none' | 'mark' | 'accept'
  /** Files without mates are to be paired with mates the lab drops later, rather than taken as merged reads. */
  layoutAnswer?: 'add-mates'
  /** Whether the primers are on the reads, as the lab said where the reads did not tell. */
  trimmingAnswer?: 'on' | 'removed'
  /** Files without mates are merged reads, as the lab said. */
  mergedAnswer?: true
  /** One sample per file pair, or several, as the lab said. */
  multiplexAnswer?: 'single' | 'combined'
  /** The lab answered the pools question; with `poolsSource: 'one'` that answer was "one pool". Unset is one pool too, unasked. */
  poolsAnswered?: boolean
  /** Where the confirmed pools came from. */
  poolsSource?: PoolsSource
  submission?: { status: 'submitted'; at: string }
  questions: WizardQuestion[]
  /** Rehydrated from browser storage: file handles are gone until the files are dropped again. */
  restored: boolean
  /** Warnings the lab went on past, by `acknowledgementKey`: they stay quiet until their content changes. */
  acknowledged?: string[]
  /** Removed groups whose items the server may still hold, by group key. */
  removals?: Record<string, WizardRemoval>
  /** Sample and file names of items the lab removed: sheet rows naming them are expected to match nothing. */
  removedNames?: string[]
  /** The pool refuses a second upload under a name it holds, so the same file dropped again takes these back. */
  releasedFiles?: WizardReleasedFile[]
  /** Groups the lab removed, by group key, until they are added back. */
  removedGroups?: Record<string, WizardRemovedGroup>
  /** Pools the wizard found empty but could not remove; left to the lab. */
  stuckPools?: string[]
  /** The labels the wizard wrote onto pools, by pool id; `''` once it took its label back. */
  poolLabels?: Record<string, string>
  /** The step the lab is on; restored with the draft. */
  step?: WizardStep
  /** What the classic submit checks and the server said about the order as it was at `signature`. */
  checks?: SubmitChecks
  /** The registry primer the lab picked where several match the samples' primers. */
  primerChoice?: string
  /** Where Review & submit stands, for this page only: never kept in browser storage. */
  submitFlow?: SubmitFlow
  /** The last attempt to keep the draft in browser storage failed (full or blocked); never kept itself. */
  storageFailed?: boolean
}

/** `pools` is the "Samples & controls" step. */
export type WizardStep = 'files' | 'pools' | 'primers' | 'metadata' | 'review'

/**
 * `one`: the lab confirmed all items in one pool; `entered`: split by hand; `dropped`: files dropped for
 * another pool; `sheet`: the sample sheet's pool column, applied or accepted; otherwise a suggestion's
 * method, accepted.
 */
export type PoolsSource = 'one' | 'entered' | 'dropped' | PoolSuggestion['method']

/** `candidates`: the registry primers to choose from, for a `primer-ambiguous` refusal. */
export type SubmitRefusal = { reason: string; message: string; candidates?: IPrimer[] }

export type SubmitChecks = {
  /** `reviewSignature` of the draft the checks ran against; stale once the draft moves on. */
  signature: string
  /** The classic submit checks refused (dry run: nothing written). */
  refusal?: SubmitRefusal
  /** The server's platform preview issues. */
  platformIssues?: ValidationIssue[]
  /** A real submit the server refused, with its issues. */
  submitIssues?: ValidationIssue[]
  submitMessages?: string[]
}

/** A read-length window; no `max` keeps every longer read. */
export type LengthWindow = { min: number; max?: number }

/** How the sampled read lengths fit the samples' length filter and the chosen primer pair. */
export type LengthCheck = {
  /** Of the sampled reads, primers off as the filter sees them. */
  median: number
  /** The chosen primer pair's window with the pipeline's fallbacks, or the fallbacks alone without a primer pair. */
  expected: LengthWindow
  outsideExpected: number
  /** The window the samples carry once submit has written its updates. */
  written: LengthWindow
  outsideWritten: number
  /** `written` is the one measured on the reads. */
  measured: boolean
}

/** The order as the review read it, with the server's previews; shown until the draft moves on. */
export type SubmitReview = {
  order: IOrder
  pools: ISamplePool[]
  primer: IPrimer | 'SKIPPED'
  lengthCheck?: LengthCheck
  preview?: OrderSubmissionPreview
  platform?: SequencingPlatformPreview
  creditCost?: OrderCreditCost
}

export type SubmitFlow = {
  /** `reviewSignature` of the draft the last check ran against; once the draft moves on, the check is due again. */
  signature?: string
  stage: 'checking' | 'checked' | 'failed' | 'submitting'
  /** What the check found; absent when the classic checks refused the order. */
  review?: SubmitReview
  /** Why the check could not run (`failed`), or what the classic checks refused. */
  message?: string
  overrideInFlight?: boolean
  /** A platform override the server refused. */
  issues?: string[]
  /** Counts refused submits, so the page opens the issue list once per refusal. */
  refusals?: number
}
